import type {
  QueryFunctionContext,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import groq from 'groq';
import { BLOG } from 'lib/constants/routes';
import {
  ASSET,
  BLOG_CARD,
  DURATION,
  EPISODE,
  PRODUCT_SLUG,
  RICH_TEXT,
  SEO,
  SLUG,
  SPEAKERS,
} from 'lib/queries';
import { client } from 'lib/sanity';
import type { BlogPost } from 'types/cms/blog/BlogPost';

type BlogPostQuery = UseQueryResult & {
  data: BlogPost;
};

type PostQueryKey = [string, string];

export const POST_KEY = 'post';

export const fetchPost = async ({
  queryKey,
}: QueryFunctionContext<PostQueryKey>) => {
  const query = groq`
  *[ _type in['timeline_article','timeline_podcast'] && slug.current == $slug][0] {
    ${SEO},
    contributors[] {
      role,
      "contributor": contributorRef -> {
        name,
        role,
        image { ${ASSET} },
        biography[] { ${RICH_TEXT} }
      }
    },
    categories,
    "type": _type,
    ${SLUG},
    "nextArticles": *[ _type in['timeline_article', 'timeline_podcast'] && slug.current != $slug && ^.categories[0] in categories[] && publishedAt < now()]|order(publishedAt desc)[0...3]{
      ${BLOG_CARD}
    },
    body[]{
      ...,
      image {
        ${ASSET}
      },
      _type == 'product' => @-> {
        ${PRODUCT_SLUG}
      },
      markDefs[]{
        ...,
        _type == "internalLink" => {
          "slug": "${BLOG}/" +@.reference->slug.current
        },
      },
    },
    ${DURATION},
    ${EPISODE},
    ${SPEAKERS},
    platforms,
    heroImage { ${ASSET} },
    heroVideoUrl,
    publishedAt,
    updatedAt,
    summary,
    title,
    keyTakeaways,
    "disclaimer": disclaimerRef->disclaimer,
  }
  `;

  const post: BlogPost = await client.fetch(query, { slug: queryKey[1] });

  return post;
};

export const usePost = (slug: string) =>
  useQuery({ queryKey: [POST_KEY, slug], queryFn: fetchPost }) as BlogPostQuery;
